/**
 * ToolItem Component
 *
 * Individual tool item with checkbox and details
 */

import React from 'react';
import { Tooltip } from '../../../components/common';

export interface ToolItemProps {
  name: string;
  displayName?: string;  // Enhanced display name (from config)
  description?: string;
  enabled: boolean;
  onToggle: () => void;
  connected?: boolean;
  transport?: string;
}

export const ToolItem: React.FC<ToolItemProps> = ({
  name,
  displayName,
  description,
  enabled,
  onToggle,
  connected,
  transport,
}) => {
  // Use displayName if provided, otherwise fallback to name
  const shownName = displayName || name;
  return (
    <div
      className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
        enabled
          ? 'bg-blue-50 border-blue-200'
          : 'bg-white border-gray-200 hover:bg-gray-50'
      }`}
    >
      {/* Checkbox */}
      <input
        type="checkbox"
        checked={enabled}
        onChange={onToggle}
        className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
      />

      {/* Tool info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm text-gray-900">{shownName}</span>

          {/* Connection status (for MCP tools) */}
          {connected !== undefined && (
            <Tooltip content={connected ? 'Connected' : 'Disconnected'}>
              <span
                className={`w-2 h-2 rounded-full ${
                  connected ? 'bg-green-500' : 'bg-red-500'
                }`}
              />
            </Tooltip>
          )}

          {/* Transport type (for MCP tools) */}
          {transport && (
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
              {transport}
            </span>
          )}
        </div>

        {/* Show technical name if different from display name */}
        {displayName && displayName !== name && (
          <p className="text-xs text-gray-400 font-mono">{name}</p>
        )}

        {description && (
          <p className="text-xs text-gray-600 mt-1 line-clamp-2">{description}</p>
        )}
      </div>
    </div>
  );
};
